import { useEffect, useState } from 'react'
import { apiService, type TenantAgent, type UserAgentNameEntry } from '@/service/api'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import { Lock, Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'

export default function AgentManagement() {
  const { t } = useTranslation()
  const { user } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const [agents, setAgents] = useState<TenantAgent[]>([])
  const [nicknamesByAgentId, setNicknamesByAgentId] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [togglingAgents, setTogglingAgents] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadAgents()
  }, [])

  const loadAgents = async () => {
    try {
      setLoading(true)
      const response = await apiService.getTenantAgents()
      setAgents(response.agents)
    } catch (err) {
      console.error('Failed to load tenant agents:', err)
      toast.error(t('common:settings.agent_management.load_error'))
    } finally {
      setLoading(false)
    }

    try {
      const namesRes = await apiService.getUserAgentNames()
      const map: Record<string, string> = {}
      namesRes.forEach((e: UserAgentNameEntry) => {
        map[e.agentId] = e.customName
      })
      setNicknamesByAgentId(map)
    } catch (err) {
      console.error('Failed to load agent nicknames:', err)
    }
  }

  const handleToggle = async (agentId: string, enabled: boolean) => {
    setTogglingAgents((prev) => new Set(prev).add(agentId))

    try {
      const updated = await apiService.updateTenantAgent(agentId, enabled)
      setAgents((prev) =>
        prev.map((agent) =>
          agent.id === agentId
            ? { ...agent, isEnabled: updated.isEnabled }
            : agent
        )
      )
      toast.success(
        enabled
          ? t('common:settings.agent_management.enabled_toast', { name: updated.name })
          : t('common:settings.agent_management.disabled_toast', { name: updated.name })
      )
    } catch (err) {
      console.error(`Failed to toggle agent ${agentId}:`, err)
      toast.error(t('common:settings.agent_management.update_error'))
    } finally {
      setTogglingAgents((prev) => {
        const next = new Set(prev)
        next.delete(agentId)
        return next
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  const provisionedAgents = agents.filter((a) => a.isProvisioned)
  const unprovisionedAgents = agents.filter((a) => !a.isProvisioned)

  return (
    <div className="space-y-6">
      {provisionedAgents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {provisionedAgents.map((agent) => {
            const isToggling = togglingAgents.has(agent.id)
            const nickname = nicknamesByAgentId[agent.id]
            return (
              <div
                key={agent.id}
                className={`relative p-5 rounded-lg border transition-all ${
                  agent.isEnabled
                    ? 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border shadow-sm'
                    : 'bg-gray-50 dark:bg-dark-muted border-gray-200 dark:border-dark-border'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-dark-accent">
                      <img
                        src={agent.image}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-text truncate">
                        {agent.name}
                        {nickname && (
                          <span className="font-normal text-gray-500">
                            {' '}(a.k.a.{' '}
                            <span className="text-blue-600 font-medium">{nickname}</span>)
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5">
                        {agent.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isToggling && (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500" />
                    )}
                    {agent.alwaysOn ? (
                      <Badge
                        variant="default"
                        className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-xs"
                      >
                        {t('common:settings.agent_management.always_on')}
                      </Badge>
                    ) : isAdmin ? (
                      <Switch
                        checked={agent.isEnabled}
                        onCheckedChange={(checked) =>
                          handleToggle(agent.id, checked)
                        }
                        disabled={isToggling}
                      />
                    ) : (
                      <Badge
                        variant={agent.isEnabled ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {agent.isEnabled ? t('common:settings.agent_management.enabled') : t('common:settings.agent_management.disabled')}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  {agent.isEnabled ? (
                    <Badge
                      variant="default"
                      className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30"
                    >
                      {t('common:settings.agent_management.active')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 dark:bg-dark-accent text-gray-500 dark:text-dark-text-muted"
                    >
                      {t('common:settings.agent_management.disabled')}
                    </Badge>
                  )}
                  {agent.isOnline && agent.isEnabled && (
                    <Badge
                      variant="outline"
                      className="text-xs text-gray-500 dark:text-dark-text-muted"
                    >
                      {t('common:settings.agent_management.online')}
                    </Badge>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {unprovisionedAgents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-dark-text-muted mb-3">
            {t('common:settings.agent_management.not_available')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {unprovisionedAgents.map((agent) => (
              <div
                key={agent.id}
                className="relative p-5 rounded-lg border border-dashed border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-muted opacity-60"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-dark-accent grayscale">
                    <img
                      src={agent.image}
                      alt={agent.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-gray-500 dark:text-dark-text-muted truncate">
                      {agent.name}
                    </h4>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {agent.description}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-400 dark:text-gray-500">
                      <Lock className="h-3 w-3" />
                      <span>{t('common:settings.agent_management.contact_support')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-400">
            {t('common:settings.agent_management.admin_only')}
          </p>
        </div>
      )}
    </div>
  )
}
